import { Component, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { ScaleType, Color } from '@swimlane/ngx-charts';
import { AbstractComponent, DateUtil, InsightsService } from 'lib-trend-core';
import { debounceTime, takeUntil } from 'rxjs';

interface ChartData {
  name: string;
  series: { name: string; value: number | string }[];
}

type CostType = 'amount_spent' | 'cost_per_delivered' | 'cost_per_url_button_click';

const DEFAULT_CURRENCY = 'USD';

interface TemplateClicked {
  type: string;
  button_content: string;
  count: number;
}

interface CostItem {
  type: CostType;
  value: number;
}

export interface TemplateAnalytics {
  delivered: number;
  read: number;
  sent: number;
  start: number;
  end: number;
  template_id: string;
  count?: number;
  clicked?: Array<TemplateClicked>;
  cost?: Array<CostItem>;
}

@Component({
    selector: 'app-template-analytics-detail',
    templateUrl: './template-analytics-detail.v2.component.html',
    // templateUrl: './template-analytics-detail.component.html',
    styleUrl: './template-analytics-detail.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TemplateAnalyticsDetailComponent extends AbstractComponent implements OnInit {
  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;
  templateId: string | number;
  templateName: string;
  templateCategory: string;
  channelId: string;

  isPicker: boolean = false;
  tabIndex: number = 0;
  selectedView: 'overview' | 'details' = 'overview';



  channelIdHometck: string = '66f6ae9d3df6da79e471b464'
  templateIdHometck: string = '1108723383552510';

  templatesData: Array<TemplateAnalytics> = [];
  totalDeliveredCount: number = 0;
  totalSentCount: number = 0;
  totalReadCount: number = 0;

  totalCostPerClick: number = 0;
  totalCostCount: number = 0;
  totalCostPerDelivery: number = 0;
  currencyCode: string = DEFAULT_CURRENCY;

  templateChart: ChartData[] = [];
  templatesDataTable: Array<any> = [];

  isDataAnalyticsExists: boolean = false;

  colorScheme: Color = {
    name: 'custom',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#5AA454', '#A10A28', '#C7B42C', '#AAAAAA']
  };

  breadcrumb = [
    { href: '/report', label: 'report.breadcrumb.reports' },
    { href: '/report/view-insights-templates', label: 'report.breadcrumb.bcTemplatesAnalytics' },
    { label: 'report.breadcrumb.bcTemplateDetails' },
  ];


  constructor(injector: Injector, private insightsService: InsightsService) {
    super(injector);

    this.route.queryParams.subscribe(params => {
      this.templateId = params['id'];
      this.templateName = params['name'];
      this.templateCategory = params['category'];
      this.channelId = params['channel'];
    });
  }

  ngOnInit(): void {
    this.setupForms();
    this.getAllAnalyticsData();
  }

  private setupForms(): void {
    const today = new Date(new Date().toISOString().slice(0, -1) + 'Z');
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setUTCDate(today.getUTCDate() - 30);

    this.formGroup = this.formBuilder.group({
      channel: this.channelId,
      category: [null],
      dateRange: this.formBuilder.group({
        start: [thirtyDaysAgo],
        end: [new Date(new Date().toISOString().slice(0, -1) + 'Z')]
      }),
      filteringParameter: this.formBuilder.group({
        metric_types: [['SENT', 'DELIVERED', 'READ', 'CLICKED', 'COST'], []],
        template_ids: [this.templateId, []],
      })
    });

    let previousValue = this.formGroup.value;

    this.formGroup.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(currentValue => {
        if (JSON.stringify(previousValue) !== JSON.stringify(currentValue)) {
          this.getAllAnalyticsData();
          previousValue = currentValue;
        }
      });
  }

  private async getAllAnalyticsData(): Promise<void> {
    const { channel, dateRange, filteringParameter } = this.formGroup.getRawValue();

    this.loadingContent = true;

    const analyticsTemplatesData = {
      start: Math.floor(dateRange.start.getTime() / 1000),
      end: Math.floor(dateRange.end.getTime() / 1000),
      filteringParameter
    };

    this.insightsService.getInsightsTemplates(channel, analyticsTemplatesData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {

          this.currencyCode = data?.currency ?? DEFAULT_CURRENCY;
          this.isDataAnalyticsExists = data.data.length > 0;
          this.templatesData = data.data[0].data_points ?? [];

          this.calculateTemplateTotals();
          this.getTemplateDataTable();
          this.buildChartData(this.templatesData);
          this.loadingContent = false;
        },
        complete: () => this.loadingContent = false,
        error: () => {
          this.loadingContent = false;
          console.error('Erro ao buscar dados de analytics');
        }
      });
  }

  calculateTemplateTotals(): void {
    this.totalDeliveredCount = this.templatesData.reduce((acc, template) => acc + (template.delivered), 0);

    this.totalSentCount = this.templatesData.reduce((acc, template) => acc + (template.sent), 0);
    this.totalReadCount = this.templatesData.reduce((acc, template) => acc + (template.read), 0);

    this.totalCostCount = this.sumCostBy('amount_spent');
    this.totalCostPerClick = this.sumCostBy('cost_per_url_button_click');
    this.totalCostPerDelivery = this.sumCostBy('cost_per_delivered');

  }

  buildChartData(data: TemplateAnalytics[]) {
    const serialMap: Record<string, ChartData> = {
      Enviadas: { name: this.t('report.templateAnalyticsDetail.sent'), series: [] },
      Entregues: { name: this.t('report.templateAnalyticsDetail.delivered'), series: [] },
      Lidas: { name: this.t('report.templateAnalyticsDetail.read'), series: [] },
      Clicadas: { name: this.t('report.templateAnalyticsDetail.clicks'), series: [] },
    };

    for (const item of data) {
      const formattedDate = DateUtil.toFormart(new Date(item.start * 1000), 'dd MMM', 'UTC');


      serialMap['Enviadas'].series.push({ name: formattedDate, value: item.sent ?? 0 });
      serialMap['Entregues'].series.push({ name: formattedDate, value: item.delivered ?? 0 });
      serialMap['Lidas'].series.push({ name: formattedDate, value: item.read ?? 0 });

      const clickedCount = Array.isArray(item.clicked) ? item.clicked.reduce((sum: number, click: TemplateClicked) => sum + (click?.count ?? 0), 0) : 0;

      serialMap['Clicadas'].series.push({ name: formattedDate, value: clickedCount });
    }

    this.templateChart = Object.values(serialMap);
  }

  getTemplateDataTable(): void {
    this.templatesDataTable = [];

    this.templatesData.forEach(data => {
      const formattedDate = DateUtil.toFormart(new Date(data.start * 1000), 'dd/MM/yyyy');

      const clickedCount = data.clicked?.reduce((sum, item) => sum + item.count, 0) ?? 0;
      const deliveredRate = data.delivered > 0 ? (data.sent / data.delivered) * 100 : 0;
      const costPerClick = this.costValue(data, 'cost_per_url_button_click');
      const costPerDelivered = this.costValue(data, 'cost_per_delivered');


      this.templatesDataTable.push({
        name: this.templateName,
        delivered: data.delivered ?? 0,
        sent: data.sent ?? 0,
        read: data.read ?? 0,
        category: this.templateCategory ?? '',
        clicked: clickedCount ?? 0,
        deliveryRate: deliveredRate.toFixed(2) + '%',
        costPerClick: costPerClick.toFixed(2),
        costPerDelivered: costPerDelivered.toFixed(2),
        // ctr: (data.read > 0 ? (data.read / data.sent) * 100 : 0).toFixed(2) + '%',
        start: formattedDate
      });
    });

  }

  openPicker(): void {
    if (this.picker) {
      this.isPicker = true;
      setTimeout(() => this.picker.open(), 200);
    } else {
      console.error('Picker is not initialized');
    }
  }

  onPeriodChange(event: any): void {
    const days = Number(event.value);
    if (!isNaN(days)) {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - days);

      this.formGroup.get('dateRange')?.patchValue({ start, end });
      this.picker?.close();
    }
  }

  dictionaryCategory(category: string) {
    const dictionary = {
      "MARKETING": "Marketing",
      "UTILITY": "Utilitário"
    }

    return dictionary[category]
  }

  /**
   * The cost of one metric inside a single data point.
   *
   * `cost` is an ARRAY of `{ type, value }`, so it has to be matched on `type`.
   * The rows used to run `Object.entries` over it and compare the ENTRY KEY —
   * which on an array is the index ("0", "1"), never a CostType — so every row
   * reported 0.00 regardless of what Meta charged.
   */
  private costValue(point: TemplateAnalytics, type: CostType): number {
    if (!Array.isArray(point?.cost)) return 0;
    return point.cost.reduce((sum, item: CostItem) => (
      item?.type === type ? sum + (item.value ?? 0) : sum
    ), 0);
  }

  private sumCostBy(type: CostType): number {
    // The guard lives in `costValue`: Meta omits `cost` entirely for WABAs on a
    // Solution Partner's credit line, and this used to call .reduce on undefined
    // and take the whole report down with a TypeError.
    return this.templatesData.reduce((acc: number, template: TemplateAnalytics) => (
      acc + this.costValue(template, type)
    ), 0);
  }
}
